//!#system	

<template>
	<div>
		<v-layout wrap>
			<v-flex xs5>
				<div style="margin-right: 20px;">
					<v-combobox 
						v-model="model.path" 
						:items="paths" 
						label="Field or JSON path to match"
						@input="updateModel()"
						@change="updateModel()"
						:error="!model.path"
					>
						<template v-slot:item="data">
							<template v-if="!pathHints[data.item]">
								<v-list-item-content v-text="data.item"></v-list-item-content>
							</template>
							<template v-else>
								<v-list-item-content>
									<v-list-item-title v-html="data.item"></v-list-item-title>
									<v-list-item-subtitle v-html="pathHints[data.item]"></v-list-item-subtitle>
								</v-list-item-content>
							</template>
						</template>
					</v-combobox>
				</div>
			</v-flex>
			<v-flex xs3>
				<div style="margin-right: 20px;">
					<v-select
						v-model="model.operator"
						:items="operatorsArrayNames"
						label="Operator"
						@change="operatorChanged"
						@input="updateModel()"
					></v-select>
				</div>
			</v-flex>
			<v-flex xs3>
				<v-text-field v-if="valueType === 'input'"
					label="Value to compare"
					v-model="model.value"
					@input="updateModel()"
					hint="Compare with bucket data by providing a path in brackets: [dataPoint.toLookAt]"
				>
					<v-icon 
						v-if="arrayValueIsSupported" 
						slot="append-outer" 
						color="red" 
						x-small 
						style="cursor: pointer;" 
						@click="changeValueType" 
						title="Change this to a list"
					>{{valueTypeIcon}}</v-icon>
				</v-text-field>
				<div v-if="valueType === 'range'">
					<v-text-field
						label="Starting range"
						v-model="model.value[0]"
						@input="updateModel()"
					></v-text-field>
					<v-text-field
						label="Ending range"
						v-model="model.value[1]"
						@input="updateModel()"
					></v-text-field>
				</div>
				<v-combobox 
					v-if="valueType === 'combo'" 
					v-model="model.value" 
					multiple 
					:small-chips="true" 
					:items="genericItems" 
					label="Items to compare" 
					:persistent-hint="true" 
					hint="Press 'Enter' to set a value" 
					@change="updateModel()"
				></v-combobox>
				<v-select 
					v-if="valueType === 'tags'"
					v-model="model.value" 
					:items="availableTags" 
					label="Tag"
					item-value="tag"
					item-text="tag"
					no-data-text="Sorry, no tags found"
				></v-select>
				<v-select 
					v-if="valueType === 'queues'"
					v-model="model.value" 
					:items="availableQueues" 
					label="Queue Code"
					item-value="queueCode"
					item-text="queueCode"
					no-data-text="Sorry, no queues found"
				></v-select>
			</v-flex>
			<v-flex xs1>
				<v-tooltip bottom>
					<template v-slot:activator="{ on }">
						<v-btn class="removePathButton" v-on="on" @click="removeField()" color="warning" x-small dark>
							<v-icon dark x-small>mdi-minus</v-icon>
						</v-btn>
					</template>
					<span>Remove this field/path</span>
				</v-tooltip>
			</v-flex>
		</v-layout>
	</div>
</template>

<script>
import apiService from '@/apiService';
import series from 'to-series';
export default {
	name: 'rulesItem',
	props: {
		routeInfo: {
			type: Object
		},
		value: {
			type: Object
		},
		bucket: {
			type: String
		}
	},
	data() {
		return {
			isLoading: false,
			paths: [],
			model: this.value.type ? this.value : {
				type: 'simple',
				path: '',
				operator: '',
				value: ''
			},
			arrayValueIsSupported: false,
			valueType: 'input',
			valueTypeIcon: 'mdi-plus',
			genericItems: [],
			pathHints: { // defined on Base.rulesFields()
				'tags': 'The current tags (array)',
				'queues[any].queueCode': 'Any of the queue codes, currently on this',
				'queues[any].singularText': 'Any of the queue (singular) descrptions on this',
				'queues[any].pluralText': 'Any of the queue (plural) descrptions on this',
				'tagged.tag': 'If "tag" is your trigger, this is the tag it is being tagged with',
				'untagged.tag': 'If "untag" is your trigger, this is the tag it is being untagged with',
				'queued.queueCode': 'If "queued" is your trigger, this is the queueCode it is being added to',
				'dequeued.queueCode': 'If "dequeue" or "dequeued" is your trigger, this is the queueCode it is being removed from',
				'user.groups[any].name': 'Any current user group name that is triggering the action'
			},
			availableQueues: [],
			availableTags: []
		}
	},
	methods: {
		getAvailableTags(done) {
			if (!this.bucket) return done();
			let [ namespace, pluralName ] = this.bucket.split('/');
			apiService.availableTags({
				namespace,
				pluralName
			}, (err, tags) => {
				if (err) return done(err);
				this.availableTags = tags;
				return done();
			});
		},
		getAvailableQueues(done) {
			if (!this.bucket) return done();
			let [ namespace, pluralName ] = this.bucket.split('/');
			apiService.availableQueues({
				namespace,
				pluralName
			}, (err, queues) => {
				if (err) return done(err);
				this.availableQueues = queues;
				return done();
			});
		},
		isLoaded() {
			return !this.isLoading;
		},
		operatorChanged() {
			if (this.model.operator === 'in the range') {
				this.arrayValueIsSupported = true;
			} else if (this.model.operator === 'contains') {
				this.arrayValueIsSupported = true;
			} else {
				this.arrayValueIsSupported = false;
			}
			if (!this.arrayValueIsSupported && this.valueType === 'combo') {
				this.valueType = 'input';
			}
			this.adaptValue();
		},
		adaptValue() {
			if (this.model.operator === 'in the range') {
				if (!Array.isArray(this.model.value)) {
					if (this.model.value) {
						this.model.value = [this.model.value, ''];
					} else {
						this.model.value = ['', ''];
					}
				}
				this.valueType = 'range';
			}
			if (this.arrayValueIsSupported && !Array.isArray(this.model.value) && this.valueType === 'combo') {
				this.model.value = [];
			} else if (!this.arrayValueIsSupported && Array.isArray(this.model.value)) {
				if (this.model.value.length >= 0) {
					this.model.value = this.model.value[0];
				} else {
					this.model.value = '';
				}
			}
		},
		adaptToList() {
			console.log('adaptToList', this.model.path);
			if (['tags', 'tagged.tag', 'untagged.tag'].includes(this.model.path)) {
				this.valueType = 'tags';
				return;
			}
			if (['queues[any].queueCode', 'queued.queueCode', 'dequeued.queueCode'].includes(this.model.path)) {
				this.valueType = 'queues';
				return;
			}
			this.valueType = 'input';
			this.adaptValue();
			this.operatorChanged();
		},
		updateModel() {
			console.log('updateModel:rulesItem', { ...this.value, ...this.model });
			this.adaptToList();
			this.$emit('input', this.model);
		},
		changeValueType() {
			console.log('changeValueType', this.valueType);
			if (this.valueType === 'input') {
				this.valueType = 'combo';
			} else if (this.valueType === 'combo') {
				this.valueType = 'input';
			}
			console.log('changeValueType', this.valueType);
			this.adaptValue();
		},
		removeField() {
			this.$emit('remove');
		},
		getJsonPaths(done) {
			let objectPathArray = this.bucket.split('/');
			let namespace = objectPathArray[0];
			let objectNamePlural = objectPathArray[1];
			apiService.rulesKeys({
				...this.routeInfo,
				pluralName: objectNamePlural,
				namespace: namespace
			}, (err, response, meta) => {
				this.paths = response;
				if (done) return done(err);
			});
		},
		getInitialData() {
			this.isLoading = true;
			series()
				.first(cb => {
					this.getJsonPaths(cb);
				})
				.next(cb => {
					this.getAvailableQueues(cb);
				})
				.next(cb => {
					this.getAvailableTags(cb);
				})
				.end(err => {
					this.isLoading = false;
				});
		},
	},
	computed: {
		operatorsList() {
			return {
				'is': '==',
				'more': '>',
				'less': '<',
				'is or more': '>=',
				'is or less': '<=',
				'contains': '>>',
				'starts with': '>.',
				'ends with': '.<',
				'in the range': '>.<',
				'is empty': '--'
			};
		},
		operatorsArrayNames() {
			return Object.keys(this.operatorsList);
		}
	},
	watch: {
		bucket: function(value, oldValue) {
			this.getInitialData();
		}
	},
	created() {
		this.getInitialData();
		this.operatorChanged();
		this.adaptToList();
	}
};
</script>

<style scoped>
.removePathButton {
	margin-left: 20px;
	margin-bottom: -50px;
}
</style>